import { useCallback, useState } from 'react'

import { fetchLibrary } from '../api/library'
import type { IndexProgress, Reading, SearchHit, SearchOrder } from '../api/search'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { navigate, useRoute } from '../frame/route'
import { Filters } from './search/Filters'
import { Progress } from './search/Progress'
import { Searching } from './search/Searching'
import { SEARCH_AREA, pathTo, searchedIn, whereItLives } from './search/place'

/**
 * The detailed search: the same search as the layer, in a room of its own.
 *
 * Not a second search. The field, the reading line and the list of hits are the same component; what this
 * adds is what needs room — the filters, the order, and how far the reading of the space has got, with
 * the control that starts it again.
 *
 * **Here the address is the truth**, exactly as it is everywhere else in MyLib and unlike in the layer: a
 * search has a link somebody can send, and the browser's back button walks out of it. Typing replaces the
 * current entry rather than adding one, so a search for eleven letters does not leave eleven entries in
 * the history.
 *
 * **It is deliberately not in the navigation.** The search is not a place you go to before you can look
 * for something — it is over everything, on ⌘K and on the magnifier in the bar. This area is where you
 * end up when a search needs more room, or when somebody sends you one.
 */
export function Search() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const searched = searchedIn(route.path)
  /*
   * What is in the field. It follows the address, and the address follows it a moment later — the
   * dependency is the *route segment*, a string, so the loader beside it is stable for as long as the
   * address is.
   */
  const [line, setLine] = useState(searched.line)
  const [reading, setReading] = useState<Reading | null>(null)
  const [progress, setProgress] = useState<IndexProgress | null>(null)

  const shelf = useLoad(useCallback((signal: AbortSignal) => fetchLibrary(signal), []))
  const folders = shelf.load.status === 'ready' ? shelf.load.value.folders : []

  const write = (written: string, order: SearchOrder = searched.order) => {
    setLine(written)
    // Replacing rather than adding: a search typed letter by letter must not become eleven entries in
    // the browser's history that somebody then has to press back through.
    navigate(SEARCH_AREA, pathTo({ line: written, order }), { replace: true })
  }

  const open = (hit: SearchHit) => {
    const to = whereItLives(hit)
    if (to === null) {
      return
    }
    navigate(to.area, to.path)
  }

  /*
   * Stable, and it has to be: the search asks again whenever this changes, and this is what it calls
   * with every answer. Written inline it would be a new function on every render, so every answer would
   * cause another question — a search that polls itself for as long as the screen is open.
   */
  const remember = useCallback((how: Reading, now: IndexProgress) => {
    setReading(how)
    setProgress(now)
  }, [])

  return (
    <Lanes
      overview={{
        label: 'Search',
        content: (
          <div className="search">
            <Searching
              shape="full"
              line={line}
              order={searched.order}
              onLine={write}
              onOpen={open}
              folders={folders}
              onRead={remember}
              controls={
                <>
                  <Filters
                    line={line}
                    reading={reading}
                    order={searched.order}
                    folders={folders}
                    onLine={write}
                    onOrder={(order) => {
                      write(line, order)
                    }}
                  />
                  <Progress progress={progress} onRebuilt={setProgress} />
                </>
              }
            />
          </div>
        ),
      }}
    />
  )
}
